/**
 * 読み込みに失敗したときの文。<b>何が起きたかを一言と、サーバの問題ならエラー番号と時刻</b>を添える。
 *
 * 「しばらく時間をおいて」だけだと、設定の誤りで毎回落ちているのか、混み合っているだけなのかが
 * 利用者にも運営にも分からない。番号と時刻があれば、問い合わせのときにログ（Application Insights）を探せる。
 * <b>内部の情報（例外の文・ID・接続先）は出さない</b>——API も問題詳細に状態と題名しか入れない（Backend の docs/09 §2.2）。
 */

export interface LoadFailure {
    /** 応答の状態コード。通信できなければ無い。 */
    readonly status?: number;
    /** 失敗した時刻。<b>文には日本時間で出す</b>（見ている端末の時間帯によらない）。 */
    readonly at: Date;
}

/** 何が起きたか（1 文）と、どうすればよいか（1 文）。 */
export interface LoadFailureText {
    readonly reason: string;
    readonly hint: string;
}

/**
 * 時刻の文字。<b>日本時間に固定する</b>——運営は日本時間でログを探す。
 * 時間帯を付けないと端末の時間帯で出て、日本時間でない端末から問い合わせが来たときに時刻が合わない。
 */
export const timeOf = (at: Date): string =>
    new Intl.DateTimeFormat("ja-JP", {
        timeZone: "Asia/Tokyo",
        year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
    }).format(at);

/** 失敗の種類ごとの文。 */
export const loadFailureText = ({ status, at }: LoadFailure): LoadFailureText => {
    if (status === undefined) {
        return {
            reason: "サーバに接続できませんでした。",
            hint: "ネットワークの状態をご確認のうえ、もう一度お試しください。",
        };
    }
    if (status === 429) {
        return {
            reason: "アクセスが集中しています。",
            hint: "しばらく時間をおいてからお試しください。",
        };
    }
    if (status >= 500) {
        return {
            reason: `サーバで問題が起きました（エラー ${String(status)}・${timeOf(at)}）。`,
            hint: "しばらく時間をおいてからお試しください。解決しないときは、この時刻を添えてお問い合わせください。",
        };
    }
    return {
        reason: `要求を受け付けられませんでした（エラー ${String(status)}）。`,
        hint: "画面を開き直してから、もう一度お試しください。",
    };
};

/** 「{何}を読み込めませんでした。」から始める、断りの枠に出す 1 つの文。 */
export const loadFailureMessage = (what: string, failure: LoadFailure): string => {
    const { reason, hint } = loadFailureText(failure);
    return `${what}を読み込めませんでした。${reason}${hint}`;
};
